import { useTranslations } from "@zapier/i18n";
import { Icon } from "@zapier/zinnia-icons";

import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  Button,
  IconButton,
  Tooltip,
} from "@zapier/design-system";

import {
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
} from "@zapier/shared";
import { FolderTypeQueryState, isPersonalFolder } from "@zapier/utils";
import { useState } from "react";
import { FolderCreationModal } from "../../../folders/FolderCreationModal";
import styles from "./CreationButton.module.css";
import {
  useAccountActionPermissions,
  useFolderActionPermissions,
} from "@zapier/client";

const MENU_ITEM_ZAP = "newZap";
const MENU_ITEM_FOLDER = "newFolder";

interface Props {
  createZapHref?: string;
  parentId?: string;
  parentName?: string;
  parentIsShared?: boolean;
}

export function CreationButton({
  createZapHref,
  parentId,
  parentName,
  parentIsShared,
}: Props) {
  const t = useTranslations("creation-button");

  const [isFolderCreationModalOpen, setIsFolderCreationModalOpen] =
    useState(false);
  const { emit } = useEmitFolderActionEvent();
  const { isSharingEnabled } = useAccountActionPermissions();
  const { canCreateZap, canCreateSubfolder } = useFolderActionPermissions({
    folderId: parentId,
  });

  const isInPersonalFolder =
    parentId !== undefined && isPersonalFolder(parentId);

  const allowCreateZap = parentId ? canCreateZap : true;
  const allowCreateFolder = parentId ? canCreateSubfolder : true;

  const newZapLabel = t("menu-items.new-zap");
  const newFolderLabel = parentId
    ? t("menu-items.new-subfolder")
    : t("menu-items.new-folder");

  const handleAction = (key: React.Key) => {
    if (key === MENU_ITEM_ZAP && createZapHref) {
      globalThis.location.href = createZapHref;
    } else if (key === MENU_ITEM_FOLDER) {
      setIsFolderCreationModalOpen(true);
      emit({
        optionSelected: FolderActionInteractionOption.CreateFolder,
        interactionRawText: FolderTypeQueryState.Personal,
      });
    }
  };

  const renderMenu = () => (
    <BetaMenuTriggerPopover>
      <BetaMenuTriggerList
        aria-label={t("labels.create-options-dropdown")}
        onAction={handleAction}
      >
        <BetaMenuTriggerItem
          id={MENU_ITEM_ZAP}
          textValue={newZapLabel}
          isDisabled={!allowCreateZap}
        >
          <span
            className={`${styles.menuText} ${!allowCreateZap && styles.disabledItem}`}
          >
            <Icon
              name="miscBoltAlt"
              size="20"
              color={allowCreateZap ? "GrayWarm6" : "GrayWarm5"}
              aria-hidden={true}
            />
            {newZapLabel}
          </span>
        </BetaMenuTriggerItem>
        {!isInPersonalFolder && (
          <BetaMenuTriggerItem
            id={MENU_ITEM_FOLDER}
            textValue={newFolderLabel}
            isDisabled={!allowCreateFolder}
          >
            <span
              className={`${styles.menuText} ${!allowCreateFolder && styles.disabledItem}`}
            >
              <Icon
                name="navFolder"
                size="20"
                color={allowCreateFolder ? "GrayWarm6" : "GrayWarm5"}
              />
              {newFolderLabel}
            </span>
          </BetaMenuTriggerItem>
        )}
      </BetaMenuTriggerList>
    </BetaMenuTriggerPopover>
  );

  return (
    <div className={styles.root}>
      <div className={styles.iconButtonWrapper}>
        <Tooltip content={t("labels.toggle-create-actions")} placement="bottom">
          <BetaMenuTriggerRoot>
            <IconButton
              aria-label={t("labels.toggle-create-actions")}
              name="formAdd"
              id="create-actions-button-mobile"
              size="medium"
              variant="ghost"
            />
            {renderMenu()}
          </BetaMenuTriggerRoot>
        </Tooltip>
      </div>

      <div className={styles.regularButtonWrapper}>
        <BetaMenuTriggerRoot>
          <Button
            variant="primary"
            id="create-actions-button"
            size="medium"
            iconPosition="start"
          >
            <Icon name="formAdd" aria-hidden={true} />
            {t("buttons.create")}
          </Button>
          {renderMenu()}
        </BetaMenuTriggerRoot>
      </div>

      <FolderCreationModal
        isOpen={isFolderCreationModalOpen}
        setIsOpen={setIsFolderCreationModalOpen}
        isSharedSelectable={!parentId && isSharingEnabled}
        parentId={parentId}
        targetParentFolderName={parentName}
        isShared={parentIsShared}
      />
    </div>
  );
}
